import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import Loader from '../../../../web/images/svg/loader.svg';

import { useRemovePerformanceMutation, useRemovePerformanceSingleCyoMutation } from '../redux/tessituraApiSlice';
import { getClassNames, isMediaMatched } from '../../../utils';
import { removeErrorMessage } from '../utils';
import { useAppSelector } from '../hooks';

const SelectionExpandable = ({
  isExpanded, performances, setIsExpanded, setFocusedIndex,
}: SelectionExpandableProps) => {
  const mediaQuery = isMediaMatched(900);

  const { cyoType } = useAppSelector((state) => state.sessionSlice);

  const [removePerformance, {
    isLoading: isRemovingASinglePerformance,
    error: errorRemovingPerformance,
  }] = useRemovePerformanceMutation();

  const [removePerformanceSingleCyo, {
    isLoading: isRemovingASinglePerformanceSingleCyo,
    error: errorRemovingPerformanceSingleCyo,
  }] = useRemovePerformanceSingleCyoMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [removedLast, setRemovedLast] = useState(0);
  const [currentRemovePerformanceLineItemId, setCurrentRemovePerformanceLineItemId] = useState(0);

  const concertSeletionRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        if (!mediaQuery) document.body.classList.remove('has-calendar-open');
        setIsExpanded(false);
      }
    };

    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [mediaQuery, setIsExpanded]);

  useEffect(() => {
    setIsLoading(isRemovingASinglePerformance || isRemovingASinglePerformanceSingleCyo);
  }, [isRemovingASinglePerformance, isRemovingASinglePerformanceSingleCyo]);

  const getErrorMessage = () => (
    errorRemovingPerformance && 'message' in errorRemovingPerformance
      ? errorRemovingPerformance.message || removeErrorMessage
      : removeErrorMessage
  );

  const getErrorSingleCyoMessage = () => (
    errorRemovingPerformanceSingleCyo && 'message' in errorRemovingPerformanceSingleCyo
      ? errorRemovingPerformanceSingleCyo.message || removeErrorMessage
      : removeErrorMessage
  );

  const handleToggleSelection = () => {
    if (!mediaQuery) document.body.classList.toggle('has-calendar-open');
    setIsExpanded(!isExpanded);
  };

  const handleRemovePerformance = async (
    packageLineNumber: number, performanceLineNumber: number, performanceId: number,
  ) => {
    try {
      setCurrentRemovePerformanceLineItemId(performanceId);
      await removePerformance({ packageLineNumber, performanceLineNumber, performanceId });
    } catch ({ error }) {
      throw new Error(`Error removing performance ${performanceId}: ${error}`);
    }
  };

  const handleRemovePerformanceSingleCyo = async (
    performanceLineNumber: number, performanceId: number,
  ) => {
    try {
      setCurrentRemovePerformanceLineItemId(performanceId);
      await removePerformanceSingleCyo({ performanceLineNumber, performanceId });
    } catch ({ error }) {
      throw new Error(`Error removing performance ${performanceId}: ${error}`);
    }
  };

  if (!performances || performances.length === 0) return <></>;

  window.addEventListener('scroll', () => {
    if (window.innerWidth <= 900) {
      const offset = window.screen.availHeight - window.innerHeight;

      if (concertSeletionRef.current) concertSeletionRef.current.style.padding = `${offset}px 0`;
    } else if (concertSeletionRef.current) concertSeletionRef.current.style.padding = '0';
  });

  return (
    <div
      ref={wrapperRef}
      className={getClassNames(null, 'c-cyop__selection-expandable-wrapper', {
        'c-cyop__selection-expandable-wrapper--expanded': isExpanded,
      })}
    >
      <div
        className="c-cyop__selection-expandable-button"
        onClick={handleToggleSelection}
        onKeyDown={handleToggleSelection}
        role="button"
        tabIndex={0}
      >
        <div className="c-cyop__selection-expandable-wrapper-icon" />
        <p className="u-show@medium">See Selection</p>
      </div>

      <div
        className={getClassNames(null, 'c-cyop__selection-content', {
          'c-cyop__selection-content--expanded': isExpanded,
        })}
        ref={concertSeletionRef}
      >
        {performances.map((performance, index) => {
          if (performance) {
            const {
              date,
              packageLineItemId,
              performanceLineItemId,
              performanceId,
              tickets,
              title,
            } = performance;

            const formattedDate = dayjs(date).format('ddd DD MMM, h:mma');
            const zone = tickets[0]?.reserve;

            const removePerformanceHandler = () => {
              setRemovedLast(performanceId);

              return cyoType === 'single-cyo'
                ? handleRemovePerformanceSingleCyo(performanceLineItemId, performanceId)
                : handleRemovePerformance(packageLineItemId, performanceLineItemId, performanceId);
            };

            const hasError = !!errorRemovingPerformance || !!errorRemovingPerformanceSingleCyo;

            return (
              <div
                className={`c-cyop__selection-performance ${hasError ? 'c-cyop__selection-performance--has-error' : ''}`}
                key={performanceLineItemId}
                onBlur={() => setFocusedIndex(null)}
                onMouseLeave={() => setFocusedIndex(null)}
                onMouseOver={() => setFocusedIndex(index)}
                onFocus={() => setFocusedIndex(index)}
              >
                <div className="c-cyop__selection-performance-content">
                  <span>{title}</span>
                  <span>{formattedDate}</span>
                  <span>{zone}</span>
                </div>

                {(!isLoading || (isLoading && removedLast !== performanceId)) && (
                  <div
                    className="c-cyop__selection-performance-remove"
                    onClick={removePerformanceHandler}
                    onKeyDown={removePerformanceHandler}
                    role="button"
                    tabIndex={0}
                  >
                    <strong>Remove</strong>
                  </div>
                )}

                {isLoading && removedLast === performanceId && (
                  <div className="c-cyop__selection-performance-remove">
                    <div className="c-loader c-loader--cyop c-loader--dark c-loader--small"><Loader /></div>
                  </div>
                )}

                {(hasError && currentRemovePerformanceLineItemId === performanceId) && (
                  <div className="c-cyop__selection-performance-error">{cyoType === 'single-cyo' ? getErrorSingleCyoMessage() : getErrorMessage()}</div>
                )}
              </div>
            );
          }

          throw Error('No performance data found');
        })}
      </div>
    </div>
  );
};

const selectionExpandablePropTypes = {
  isExpanded: PropTypes.bool.isRequired,
  performances: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string.isRequired,
    packageLineItemId: PropTypes.number.isRequired,
    performanceLineItemId: PropTypes.number.isRequired,
    performanceId: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    tickets: PropTypes.arrayOf(PropTypes.shape({
      reserve: PropTypes.string.isRequired,
    })).isRequired,
  })).isRequired,
  setFocusedIndex: PropTypes.func.isRequired,
  setIsExpanded: PropTypes.func.isRequired,
};

SelectionExpandable.propTypes = selectionExpandablePropTypes;

type SelectionExpandableProps = PropTypes.InferProps<typeof selectionExpandablePropTypes>;

export default SelectionExpandable;
